import { Box, Typography, useTheme } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { ReactNode } from 'react';
import { getYCenterStyles } from 'vig-ui-library';

export type SectionProps = {
    /** Icon shown to the left of the title in the header. Sized 32x32 by convention. */
    icon?: ReactNode;
    /** Section title rendered next to the icon. String becomes h6/600. */
    title?: ReactNode;
    /** Optional content rendered on the right side of the header (e.g. action link). */
    headerRight?: ReactNode;
    /** Section body. */
    children: ReactNode;
    /** Style applied to the outer card. */
    sx?: SxProps<Theme>;
    /** Style applied to the section header (only rendered when icon/title/headerRight is present). */
    headerSx?: SxProps<Theme>;
    /** Style applied to the body container (overrides defaults like padding/gap). */
    bodySx?: SxProps<Theme>;
    /** Removes the default body padding (useful for content that manages its own paddings, e.g. ParameterItem list). */
    disableBodyPadding?: boolean;
    /** Disables the default vertical gap between body children. */
    disableBodyGap?: boolean;
};

/**
 * White "card" section on a gray page background.
 *
 * Mirrors the visual chrome used by `RequestFormLib` sections (e.g. `MagicLinkSentView`,
 * `UploadIdCardView`): white surface, 7px radius, 24px padding, divider line
 * between header and body. The component is layout-agnostic - the caller composes the
 * body via children.
 */
const Section = ({
    icon,
    title,
    headerRight,
    children,
    sx,
    headerSx,
    bodySx,
    disableBodyPadding = false,
    disableBodyGap = false,
}: SectionProps) => {
    const theme = useTheme();
    const hasHeader = !!icon || !!title || !!headerRight;

    return (
        <Box
            className="magic-link-section"
            sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                backgroundColor: theme.palette.background.default,
                borderRadius: '7px',
                overflow: 'hidden',
                ...sx,
            }}
        >
            {hasHeader && (
                <Box
                    className="magic-link-section-header"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: theme.spacing(2),
                        padding: '24px',
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        ...headerSx,
                    }}
                >
                    <Box
                        sx={{
                            ...getYCenterStyles(),
                            gap: theme.spacing(2),
                            justifyContent: 'flex-start',
                            minWidth: 0,
                            flex: 1,
                        }}
                    >
                        {icon && (
                            <Box sx={{ display: 'flex', flexShrink: 0, lineHeight: 0 }}>
                                {icon}
                            </Box>
                        )}
                        {title && (
                            typeof title === 'string'
                                ? (
                                    <Typography
                                        variant="h6"
                                        component="p"
                                        sx={{ fontWeight: 600, minWidth: 0, overflowWrap: 'anywhere' }}
                                    >
                                        {title}
                                    </Typography>
                                )
                                : title
                        )}
                    </Box>
                    {headerRight && (
                        <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                            {headerRight}
                        </Box>
                    )}
                </Box>
            )}

            <Box
                className="magic-link-section-body"
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    ...(disableBodyPadding ? {} : { padding: '24px' }),
                    ...(disableBodyGap ? {} : { gap: 2 }),
                    ...bodySx,
                }}
            >
                {children}
            </Box>
        </Box>
    );
};

export default Section;
